import { Component, computed, DestroyRef, effect, inject, OnInit, signal, untracked } from "@angular/core";
import { ActivatedRoute, Params, Router } from "@angular/router";

import { NgTemplateOutlet } from "@angular/common";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { isDefined, isPropertyDefined } from "@recursyve/nice-ts-utils";
import { SvgIconComponent } from "angular-svg-icon";
import { combineLatest, filter, switchMap, tap } from "rxjs";
import {
    sanitizeDirectusArray,
    sanitizeDirectusRelationArray
} from "../../../../common/utils/sanitize-directus-object";
import { ItemsAdBlock } from "../../../api/packbo/models/items-ad-block";
import { ItemsProductCategories } from "../../../api/packbo/models/items-product-categories";
import { SeoService } from "../../../services/seo.service";
import { AdBlockComponent } from "../../components/ad-block/ad-block.component";
import { HeaderBlockComponent } from "../../components/header-block/header-block.component";
import { PaginatorComponent } from "./components/paginator/paginator.component";
import { SearchComponent, SearchOutput } from "./components/search/search.component";
import { SubcategoryCardComponent } from "./components/subcategory-card/subcategory-card.component";
import { SubcategorySidebarComponent } from "./components/subcategory-sidebar/subcategory-sidebar.component";
import { SupplierCardShimmerComponent } from "./components/supplier-card/components/supplier-card-shimmer/supplier-card-shimmer.component";
import { SupplierCardComponent } from "./components/supplier-card/supplier-card.component";
import { CategoryService } from "./store/category.service";
import { CategoryStore } from "./store/category.store";

type MobileLayoutItem = {
    type: "subcategories" | "ad";
    subcategories?: ItemsProductCategories[];
    adBlock?: ItemsAdBlock;
};

const SUBCATEGORIES_THRESHOLD = 5;
const SUBCATEGORIES_GROUP_SIZE = 5;
const MAX_AD_BLOCKS = 2;

@Component({
    selector: "packbo-category",
    templateUrl: "./category.template.html",
    providers: [CategoryService, CategoryStore],
    imports: [
        SvgIconComponent,
        TranslocoPipe,
        HeaderBlockComponent,
        AdBlockComponent,
        SubcategoryCardComponent,
        SubcategorySidebarComponent,
        NgTemplateOutlet,
        SupplierCardComponent,
        SupplierCardShimmerComponent,
        PaginatorComponent,
        SearchComponent
    ]
})
export class CategoryComponent implements OnInit {
    private readonly categoryService = inject(CategoryService);
    private readonly categoryStore = inject(CategoryStore);
    private readonly destroyRef = inject(DestroyRef);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly seoService = inject(SeoService);
    private readonly transloco = inject(TranslocoService);

    public readonly subcategories = this.categoryStore.subcategories;
    public readonly filteredSubcategories = this.categoryStore.filteredSubcategories;
    public readonly adBlocks = this.categoryStore.adBlocks;
    public readonly headerBlock = this.categoryStore.headerBlock;
    public readonly suppliers = this.categoryStore.suppliers;
    public readonly loading = this.categoryStore.loading;
    public readonly supplierImagesLoading = this.categoryStore.supplierImagesLoading;
    public readonly pagination = this.categoryStore.pagination;
    public readonly itemsCertifications = this.categoryStore.itemsCertifications;
    public readonly currentCertifications = this.categoryStore.certifications;
    public readonly category = this.categoryStore.category;

    protected readonly searchValue = signal<string | null>(null);
    protected readonly postalCode = signal<string | null>(null);
    protected readonly distance = signal<{ value: number; label: string } | null>(null);
    protected readonly selectedSubcategoryId = signal<number | null>(null);
    protected readonly currentPage = signal(this.pagination().currentPage);
    protected readonly isEcoFriendly = signal<boolean>(false);
    protected readonly selectedCertificationsIds = signal<number[]>([]);
    protected readonly userPostalCode = signal<string | null>(null);
    private readonly previousSubcategoryId = signal<number | null>(null);

    protected readonly shouldShowSuppliers = computed(() => {
        return this.selectedSubcategoryId() !== null;
    });
    protected readonly shouldShowShimmer = computed(() => {
        return this.loading() || this.supplierImagesLoading();
    });
    protected readonly shouldShowPagination = computed(() => {
        return this.shouldShowSuppliers() && this.pagination().totalPages > 1;
    });
    protected readonly mobileLayout = computed(() => {
        const filteredSubcategories = this.filteredSubcategories();
        const adBlocks = this.adBlocks();

        if (!filteredSubcategories.length) {
            return [];
        }

        return filteredSubcategories.length < SUBCATEGORIES_THRESHOLD
            ? this.displayAllInOneGroup(filteredSubcategories, adBlocks)
            : this.displayInGroupsOfFive(filteredSubcategories, adBlocks);
    });
    protected readonly hasActiveSearchFilters = computed(() => {
        return !!(
            this.searchValue() ||
            this.postalCode() ||
            this.distance() ||
            this.selectedCertificationsIds().length > 0 ||
            this.isEcoFriendly()
        );
    });

    constructor() {
        effect(() => {
            const currentPage = this.currentPage();
            const subcategoryId = this.selectedSubcategoryId();
            const previousSubcategoryId = this.previousSubcategoryId();

            untracked(() => {
                this.categoryStore.setPagination({ currentPage });

                const subcategoryChanged = subcategoryId !== previousSubcategoryId;

                if (subcategoryChanged) {
                    this.previousSubcategoryId.set(subcategoryId);
                }

                if (subcategoryId !== null) {
                    this.router.navigate([], {
                        queryParams: {
                            subcategoryId: subcategoryId
                        }
                    });

                    if (subcategoryChanged) {
                        this.resetSearchState();
                        this.currentPage.set(1);
                    }

                    const hasSearchFilters = this.hasActiveSearchFilters();

                    if (hasSearchFilters) {
                        untracked(() => {
                            this.performSearch(currentPage);
                        });
                    } else {
                        untracked(() => {
                            this.fetchSuppliersBySubcategoryId(subcategoryId, currentPage, false);
                        });
                    }
                } else {
                    this.router.navigate([], { queryParams: { subcategoryId: null } });
                    this.filterSubcategoriesByName(null);
                }
            });
        });

        effect(() => {
            this.isEcoFriendly(); // This is intended to trigger the effect
            const subcategoryId = this.selectedSubcategoryId();

            if (subcategoryId !== null) {
                untracked(() => {
                    this.currentPage.set(1);
                    this.performSearch();
                });
            }
        });
    }

    public ngOnInit(): void {
        this.route.params
            .pipe(
                filter((params) => isPropertyDefined(params, "slug")),
                switchMap(({ slug }: Params) =>
                    combineLatest([
                        this.categoryService.fetchCategory(slug),
                        this.categoryService.fetchSubcategories(slug),
                        this.categoryService.fetchAds(),
                        this.categoryService.fetchCertifications()
                    ])
                ),
                tap(() => this.updateSeoTags()),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
            const subcategoryId = params["subcategoryId"];
            if (isDefined(subcategoryId)) {
                this.selectedSubcategoryId.set(+subcategoryId);
            } else {
                this.selectedSubcategoryId.set(null);
            }
        });

        this.transloco.langChanges$
            .pipe(
                tap(() => this.updateSeoTags()),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    public onPageChange(page: number): void {
        this.currentPage.set(page);
    }

    public onSupplierImagesLoaded(): void {
        this.categoryStore.setSupplierImagesLoading(false);
    }

    public filterSubcategoriesByName(search: string | null): void {
        if (!isDefined(search)) {
            const allSubcategories = this.categoryStore.subcategories();
            this.categoryStore.setFilteredSubcategories(allSubcategories);
            return;
        }

        const subcategories = this.categoryStore.subcategories();
        const filteredSubcategories = subcategories.filter((subcategory) => {
            const titles = sanitizeDirectusRelationArray(subcategory.translations_refs, "title").filter(isDefined);
            return titles.some((title) => title.toLowerCase().includes(search));
        });

        this.categoryStore.setFilteredSubcategories(filteredSubcategories);
    }

    public getSuppliers(searchOutput: SearchOutput): void {
        const { search, postalCode, distance, certifications } = searchOutput;
        const subcategoryId = this.selectedSubcategoryId();
        if (!subcategoryId) {
            return;
        }

        this.searchValue.set(search);
        this.postalCode.set(postalCode);
        this.distance.set(distance ? { value: parseInt(distance), label: `${distance}km` } : null);
        this.selectedCertificationsIds.set(certifications ?? []);
        this.userPostalCode.set(postalCode);
        this.currentPage.set(1);

        const hasSearchTerm = isDefined(search);
        const hasPostalCode = isDefined(postalCode);
        const hasDistance = isDefined(distance);
        const hasCertifications = certifications && certifications.length > 0;
        const hasAnyFilters = hasSearchTerm || hasPostalCode || hasDistance || hasCertifications;

        if (hasAnyFilters) {
            this.performSearch(1);
        } else {
            this.fetchSuppliersBySubcategoryId(subcategoryId, 1);
        }
    }

    private displayAllInOneGroup(
        subcategories: ItemsProductCategories[],
        adBlocks: ItemsAdBlock[]
    ): MobileLayoutItem[] {
        const contents: MobileLayoutItem[] = [
            {
                type: "subcategories",
                subcategories: subcategories
            }
        ];

        for (let i = 0; i < Math.min(MAX_AD_BLOCKS, adBlocks.length); i++) {
            if (adBlocks[i]) {
                contents.push({ type: "ad", adBlock: adBlocks[i] });
            }
        }

        return contents;
    }

    private displayInGroupsOfFive(
        subcategories: ItemsProductCategories[],
        adBlocks: ItemsAdBlock[]
    ): MobileLayoutItem[] {
        const contents: MobileLayoutItem[] = [];

        for (let i = 0; i < subcategories.length; i += SUBCATEGORIES_GROUP_SIZE) {
            const subcategoriesBlock = subcategories.slice(i, i + SUBCATEGORIES_GROUP_SIZE);
            contents.push({
                type: "subcategories",
                subcategories: subcategoriesBlock
            });

            const adIndex = Math.floor(i / SUBCATEGORIES_GROUP_SIZE);
            if (adIndex < MAX_AD_BLOCKS && adBlocks[adIndex]) {
                contents.push({
                    type: "ad",
                    adBlock: adBlocks[adIndex]
                });
            }
        }

        return contents;
    }

    private fetchSuppliersBySubcategoryId(subcategoryId: number, page = 1, resetSearch = true): void {
        if (resetSearch) {
            this.resetSearchState();
        }

        this.categoryService.reset();
        this.categoryStore.setSupplierImagesLoading(true);
        this.categoryService.getSuppliersBySubcategoryId(subcategoryId, page).subscribe(() => {
            if (this.suppliers().length === 0) {
                this.categoryStore.setSupplierImagesLoading(false);
            }
        });
    }

    private resetSearchState(): void {
        this.searchValue.set(null);
        this.postalCode.set(null);
        this.userPostalCode.set(null);
        this.distance.set(null);
        this.isEcoFriendly.set(false);
        this.selectedCertificationsIds.set([]);
    }

    private updateSeoTags(): void {
        const category = this.category();
        if (category) {
            const translations = sanitizeDirectusArray(category.translations_refs).map((t) => ({
                languages_code: t.languages_code,
                title: t.title,
                meta_description: t.description,
                slug: t.slug
            }));

            this.seoService.updateMetaTags(
                {
                    translations,
                    noIndex: false,
                    noFollow: false,
                    isPrivate: false
                },
                this.router.url
            );
        }
    }

    private performSearch(page = this.currentPage()): void {
        const subcategoryId = this.selectedSubcategoryId();
        if (!subcategoryId) return;

        this.categoryService
            .searchSuppliers({
                subcategoryId,
                searchTerm: this.searchValue() ?? "",
                postalCode: this.postalCode() ?? "",
                distance: this.distance()?.value?.toString() ?? "",
                certifications: this.selectedCertificationsIds() ?? [],
                isEcoFriendly: this.isEcoFriendly(),
                page
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
    }
}
